import { Component, OnInit, Output, EventEmitter } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';
import { BidUnitService } from 'src/app/services/bid-unit/bid-unit.service';
import Swal from 'sweetalert2';

declare var bootstrap: any;

@Component({
  selector: 'app-update-bid-unit',
  templateUrl: './update-bid-unit.component.html',
  styleUrls: ['./update-bid-unit.component.scss']
})
export class UpdateBidUnitComponent extends DestructorComponent implements OnInit {

  @Output() bidUnitUpdated = new EventEmitter<void>();

  bidUnitForm!: FormGroup;
  isLoading = false;
  selectedBidUnit: any = null;

  constructor(
    private fb: FormBuilder,
    private _bidUnitService: BidUnitService
  ) {
    super();
    this.initializeForm();
  }

  ngOnInit(): void {
    this.setupModalListener();
  }

  private initializeForm(): void {
    this.bidUnitForm = this.fb.group({
      name: [{ value: '', disabled: true }],
      company_name: [{ value: '', disabled: true }],
      group: [{ value: '', disabled: true }],
      number_of_units: ['', [Validators.required, Validators.min(1)]]
    });
  }

  private setupModalListener(): void {
    const modalElement = document.getElementById('kt_modal_update_bid_unit');
    if (modalElement) {
      modalElement.addEventListener('shown.bs.modal', () => {
        if (this.selectedBidUnit) {
          this.populateForm(this.selectedBidUnit);
        }
      });

      modalElement.addEventListener('hidden.bs.modal', () => {
        this.resetForm();
      });
    }
  }

  openModal(bidUnit: any): void {
    this.selectedBidUnit = bidUnit;
    const modalElement = document.getElementById('kt_modal_update_bid_unit');
    if (modalElement) {
      const modal = new bootstrap.Modal(modalElement);
      modal.show();
    }
  }

  private populateForm(bidUnit: any): void {
    this.bidUnitForm.patchValue({
      name: bidUnit.name || '',
      company_name: bidUnit.company_name || '',
      group: bidUnit.group || '',
      number_of_units: bidUnit.number_of_units || ''
    });
  }

  onSubmit(): void {
    if (this.bidUnitForm.valid && this.selectedBidUnit) {
      this.isLoading = true;

      const updateData = {
        number_of_units: this.bidUnitForm.get('number_of_units')?.value
      };

      this.subscriptions.push(
        this._bidUnitService.UpdateData(this.selectedBidUnit._id || this.selectedBidUnit.doc_id, updateData).subscribe({
          next: (response) => {
            this.isLoading = false;
            if (response.status) {
              Swal.fire({
                title: 'Success!',
                text: 'Bid unit updated successfully',
                icon: 'success',
                confirmButtonText: 'OK',
                buttonsStyling: false,
                customClass: {
                  confirmButton: 'btn fw-bold btn-primary'
                }
              }).then(() => {
                this.closeModal();
                this.bidUnitUpdated.emit();
              });
            } else {
              this.showError(response.message || 'Failed to update bid unit');
            }
          },
          error: (error) => {
            this.isLoading = false;
            console.error('Error updating bid unit:', error);
            const errorMessage = error?.error?.message || 'An error occurred while updating bid unit';
            this.showError(errorMessage);
          }
        })
      );
    } else {
      this.markFormAsTouched();
    }
  }

  private showError(message: string): void {
    Swal.fire({
      title: 'Error!',
      text: message,
      icon: 'error',
      confirmButtonText: 'OK',
      buttonsStyling: false,
      customClass: {
        confirmButton: 'btn fw-bold btn-primary'
      }
    });
  }

  private markFormAsTouched(): void {
    Object.keys(this.bidUnitForm.controls).forEach(key => {
      const control = this.bidUnitForm.get(key);
      control?.markAsTouched();
    });
  }

  closeModal(): void {
    const modalElement = document.getElementById('kt_modal_update_bid_unit');
    if (modalElement) {
      const modal = bootstrap.Modal.getInstance(modalElement);
      if (modal) {
        modal.hide();
      }
    }
  }

  private resetForm(): void {
    this.bidUnitForm.reset();
    this.selectedBidUnit = null;
    this.isLoading = false;
  }

  // Form validation helpers
  isFieldInvalid(fieldName: string): boolean {
    const field = this.bidUnitForm.get(fieldName);
    return !!(field && field.invalid && (field.dirty || field.touched));
  }

  getFieldError(fieldName: string): string {
    const field = this.bidUnitForm.get(fieldName);
    if (field?.errors) {
      if (field.errors['required']) {
        return 'Number of units is required';
      }
      if (field.errors['min']) {
        return 'Number of units must be at least 1';
      }
    }
    return '';
  }
}
